import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    await api.post("/auth/login", { email, password });
    await checkAuth();
  };

  const register = async (name, email, password) => {
    await api.post("/auth/register", { name, email, password });
    await checkAuth();
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed, clearing local session anyway", e);
    }
    setUser(false);
  };

  const billing = user?.billing || null;
  const readOnly = !!billing?.read_only;
  const value = useMemo(
    () => ({ user, setUser, loading, login, register, logout, checkAuth, apiErr, billing, readOnly }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, checkAuth]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
